"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.OrCADXProjectMetadataKey = exports.OrCADXFileType = void 0;
exports.getOrCADXFileType = getOrCADXFileType;
exports.validateOrCADXFile = validateOrCADXFile;
exports.detectOrcadProjects = detectOrcadProjects;
const sigma_engine_shared_1 = require("@cadstrom/sigma-engine-shared");
const project_detection_1 = require("../project-detection");
const opj_1 = require("./parsers/opj");
const ONLParser_1 = require("./parsers/onl/ONLParser");
const xmlutil_1 = require("./common/xmlutil");
const LogicalSchemaClasses_1 = require("./parsers/dsnxml/LogicalSchemaClasses");
const dsncfb_1 = require("./parsers/dsncfb");
const OrcadDsnCfbLogicalSchemaConverter_1 = require("./parsers/dsncfb/OrcadDsnCfbLogicalSchemaConverter");
var OrCADXFileType;
(function (OrCADXFileType) {
    OrCADXFileType["OrCADCaptureProject"] = ".opj";
    OrCADXFileType["OrCADCaptureSchematic"] = ".dsn";
    OrCADXFileType["OrCADCaptureSchematicXml"] = ".xml";
    OrCADXFileType["OrCADCaptureInternalNetlist"] = ".onl";
})(OrCADXFileType || (exports.OrCADXFileType = OrCADXFileType = {}));
var OrCADXProjectMetadataKey;
(function (OrCADXProjectMetadataKey) {
    OrCADXProjectMetadataKey["FileType"] = "file-type";
    OrCADXProjectMetadataKey["FileVersion"] = "file-version";
    OrCADXProjectMetadataKey["LogicalSchema"] = "logical-schema";
    OrCADXProjectMetadataKey["LogicalSchemaDiagnostics"] = "logical-schema-diagnostics";
    OrCADXProjectMetadataKey["ONLNetlist"] = "onl-netlist";
})(OrCADXProjectMetadataKey || (exports.OrCADXProjectMetadataKey = OrCADXProjectMetadataKey = {}));
const XML_SNIFF_HEAD_CHARS = 4096;
function looksLikeOrcadDsnXml(fileContents) {
    const head = typeof fileContents === 'string' ?
        fileContents.slice(0, XML_SNIFF_HEAD_CHARS)
        : new TextDecoder().decode(fileContents.subarray(0, XML_SNIFF_HEAD_CHARS));
    const lower = head.toLowerCase();
    return /<design[\s>]/.test(lower) && lower.includes('dsn.xsd');
}
function getOrCADXFileType(filename, fileContents) {
    const path = filename.split('/');
    if (path.length < 1) {
        return false;
    }
    const parts = path[path.length - 1].split('.');
    if (parts.length < 1) {
        return false;
    }
    let ext = parts[parts.length - 1].toLowerCase();
    if (parts.length >= 1) {
        ext = '.' + ext;
    }
    switch (ext.toLowerCase().trim()) {
        case OrCADXFileType.OrCADCaptureProject:
            return OrCADXFileType.OrCADCaptureProject;
        case OrCADXFileType.OrCADCaptureSchematic:
            return OrCADXFileType.OrCADCaptureSchematic;
        case OrCADXFileType.OrCADCaptureSchematicXml:
            if (fileContents !== undefined && !looksLikeOrcadDsnXml(fileContents)) {
                return false;
            }
            return OrCADXFileType.OrCADCaptureSchematicXml;
        case OrCADXFileType.OrCADCaptureInternalNetlist:
            return OrCADXFileType.OrCADCaptureInternalNetlist;
    }
    return false;
}
function orcadSemanticType(type) {
    switch (type) {
        case OrCADXFileType.OrCADCaptureProject:
            return 'project';
        case OrCADXFileType.OrCADCaptureSchematic:
        case OrCADXFileType.OrCADCaptureSchematicXml:
            return 'schematic';
        case OrCADXFileType.OrCADCaptureInternalNetlist:
            return 'netlist';
        default:
            return 'other';
    }
}
async function validateOrCADXFile(filename, fileContents) {
    const type = getOrCADXFileType(filename, fileContents);
    if (!type) {
        return {
            isValid: false,
            errors: [`Invalid Cadence OrCAD X file or contents ${filename}`],
        };
    }
    const semanticType = orcadSemanticType(type);
    let validationResult;
    try {
        switch (type) {
            case OrCADXFileType.OrCADCaptureProject:
                validationResult = await validateOrCADXProjectFile(filename, fileContentsToString(fileContents));
                break;
            case OrCADXFileType.OrCADCaptureSchematic:
                validationResult = await validateOrCADXSchematicDSNFile(filename, fileContents);
                break;
            case OrCADXFileType.OrCADCaptureSchematicXml:
                validationResult = await validateOrCADXSchematicXMLFile(filename, fileContentsToString(fileContents));
                break;
            case OrCADXFileType.OrCADCaptureInternalNetlist:
                validationResult = await validateOrCADXInternalNetlistFile(filename, fileContentsToString(fileContents));
                break;
            default:
                return {
                    isValid: true,
                    data: {
                        semanticType,
                        fileFormat: {
                            cadTool: 'orcad',
                            type: type,
                        },
                        referencedFiles: [],
                        metadata: {},
                    },
                };
        }
    }
    catch (e) {
        return {
            isValid: false,
            semanticType,
            errors: [`Invalid Cadence OrCAD X contents ${filename}`, e instanceof Error ? e.message : String(e)],
            messageResults: e instanceof Error ? [(0, sigma_engine_shared_1.errorToMessageResult)(e)] : undefined,
        };
    }
    if (!validationResult) {
        return {
            isValid: false,
            semanticType,
            errors: [`Invalid Cadence OrCAD X contents ${filename}`],
        };
    }
    return {
        isValid: true,
        data: {
            semanticType: semanticType,
            fileFormat: {
                cadTool: 'orcad',
                type: type,
                version: validationResult.version,
            },
            referencedFiles: validationResult.referencedFiles,
            metadata: validationResult.metadata,
        },
    };
}
function fileContentsToBytes(fileContents) {
    if (fileContents instanceof Uint8Array) {
        return fileContents;
    }
    return Uint8Array.from(Array.from(fileContents).map((char) => char.charCodeAt(0) & 0xff));
}
function fileContentsToString(fileContents) {
    if (typeof fileContents === 'string') {
        return fileContents;
    }
    return new TextDecoder().decode(fileContents);
}
function validateOrCADXSchematicDSNFile(filename, fileContents) {
    if (!filename.toLowerCase().endsWith(OrCADXFileType.OrCADCaptureSchematic)) {
        return undefined;
    }
    const diagnostics = [];
    const design = (0, dsncfb_1.parseOrCadDsnCfbLogicalSchema)(fileContentsToBytes(fileContents), {
        sourceFileName: filename,
        diagnostics,
    });
    const referencedFiles = collectLiveReferencedFiles(design);
    const diagnosticInnerResults = diagnostics.map((diagnostic) => ({
        type: diagnostic.type ?? 'orcad-parsing',
        message: diagnostic.message,
        severity: BOUNDARY_LEVEL_BY_DIAGNOSTIC_SEVERITY[diagnostic.severity],
    }));
    return {
        version: undefined,
        referencedFiles,
        warnings: diagnostics.length > 0 ?
            {
                type: 'orcad-parsing',
                message: `OrCAD DSN CFB parsing completed with ${diagnostics.length} diagnostic(s)`,
                severity: diagnosticInnerResults.reduce((highest, result) => (0, sigma_engine_shared_1.getHighestBoundaryLevel)(highest, result.severity ?? sigma_engine_shared_1.BoundaryLevel.Recommendation), sigma_engine_shared_1.BoundaryLevel.Recommendation),
                innerResults: diagnosticInnerResults,
            }
            : {
                type: 'orcad-parsing',
                message: 'OrCAD DSN CFB parsing completed',
                severity: sigma_engine_shared_1.BoundaryLevel.Recommendation,
                innerResults: [],
            },
        metadata: {
            [OrCADXProjectMetadataKey.LogicalSchema]: design,
            [OrCADXProjectMetadataKey.LogicalSchemaDiagnostics]: diagnosticInnerResults,
        },
    };
}
const BOUNDARY_LEVEL_BY_DIAGNOSTIC_SEVERITY = {
    [OrcadDsnCfbLogicalSchemaConverter_1.OrcadDsnCfbLogicalSchemaConverter.DiagnosticSeverity.Info]: sigma_engine_shared_1.BoundaryLevel.Recommendation,
    [OrcadDsnCfbLogicalSchemaConverter_1.OrcadDsnCfbLogicalSchemaConverter.DiagnosticSeverity.Warning]: sigma_engine_shared_1.BoundaryLevel.Warning,
    [OrcadDsnCfbLogicalSchemaConverter_1.OrcadDsnCfbLogicalSchemaConverter.DiagnosticSeverity.Error]: sigma_engine_shared_1.BoundaryLevel.Error,
};
function collectLiveReferencedFiles(design) {
    const referencedFiles = new Set();
    for (const schematic of design.Schematic) {
        for (const page of schematic.Page) {
            for (const inst of page.DrawnInst ?? []) {
                if (inst.ContentsLibName) {
                    referencedFiles.add(inst.ContentsLibName.replace(/\\/g, '/').replace(/^\.\//, ''));
                }
            }
            for (const inst of page.PartInst ?? []) {
                if (inst.ContentsLibName) {
                    referencedFiles.add(inst.ContentsLibName.replace(/\\/g, '/').replace(/^\.\//, ''));
                }
            }
        }
    }
    return Array.from(referencedFiles);
}
function validateOrCADXProjectFile(filename, fileContents) {
    if (!filename.toLowerCase().endsWith(OrCADXFileType.OrCADCaptureProject)) {
        return undefined;
    }
    try {
        const result = (0, opj_1.parseOrCADCaptureProject)(fileContents);
        if (!result) {
            return undefined;
        }
        const referencedFiles = new Set();
        for (const path of result.project.globalState?.fileView?.paths ?? []) {
            if (path.fsPath) {
                referencedFiles.add(path.fsPath);
            }
        }
        return {
            referencedFiles: Array.from(referencedFiles),
            metadata: {
                [OrCADXProjectMetadataKey.FileType]: `OrCAD Capture ${result.project.projectType ?? 'Unknown'} Project`,
                [OrCADXProjectMetadataKey.FileVersion]: result.project.projectVersion ?? 'unknown',
            },
            version: result.project.projectVersion,
            warnings: result.processingResults,
        };
    }
    catch (e) {
        return undefined;
    }
}
function validateOrCADXSchematicXMLFile(filename, fileContents) {
    const parsed = (0, xmlutil_1.ParseXml)(fileContents);
    if (!parsed.Design?.['$xsi:noNamespaceSchemaLocation'] ||
        !/(\\|\/)dsn\.xsd$/.test(parsed.Design['$xsi:noNamespaceSchemaLocation'])) {
        throw new Error('Not a valid OrCAD DSN XML file, or unknown schema version');
    }
    const validationOpts = {
        processingResults: {
            type: 'orcad-parsing',
            message: 'OrCAD DSN XML parsing completed',
            innerResults: [],
            severity: sigma_engine_shared_1.BoundaryLevel.Recommendation,
        },
    };
    const design = LogicalSchemaClasses_1.OrCadDSNClasses.Design.FromXml(parsed.Design, validationOpts);
    const referencedFiles = collectLiveReferencedFiles(design);
    const versionMatch = /\\orcadx_([0-9.]+)\\/.exec(parsed.Design['$xsi:noNamespaceSchemaLocation']);
    let version;
    if (versionMatch) {
        version = versionMatch[1];
    }
    (0, sigma_engine_shared_1.computeAggragateMessageResultSeverity)(validationOpts.processingResults);
    if (validationOpts.processingResults.severity !== sigma_engine_shared_1.BoundaryLevel.Recommendation) {
        validationOpts.processingResults.message = `OrCAD DSN XML parsing completed with ${validationOpts.processingResults.severity}s`;
    }
    return {
        version: version,
        referencedFiles,
        warnings: validationOpts.processingResults,
        metadata: {
            [OrCADXProjectMetadataKey.LogicalSchema]: design,
        },
    };
}
function detectOrcadProjects(files, fileDetails) {
    const schematicDirStems = new Set(fileDetails
        .filter((d) => d.cadTool === 'orcad' && d.semanticType === 'schematic')
        .map((d) => `${(0, project_detection_1.normalizePathForCompare)((0, project_detection_1.dirnameForward)(d.relativePath))}/${(0, project_detection_1.basenameWithoutExtension)(d.relativePath).toLowerCase()}`));
    const opjFiles = fileDetails
        .filter((d) => d.cadTool === 'orcad' && d.relativePath.toLowerCase().endsWith(OrCADXFileType.OrCADCaptureProject))
        .map((d) => ({ relativePath: d.relativePath, normDir: (0, project_detection_1.normalizePathForCompare)((0, project_detection_1.dirnameForward)(d.relativePath)) }));
    const sidecarOpjPaths = new Set(opjFiles
        .filter((opj) => {
        const stem = (0, project_detection_1.basenameWithoutExtension)(opj.relativePath).toLowerCase();
        if (!schematicDirStems.has(`${opj.normDir}/${stem}`)) {
            return false;
        }
        return opjFiles.some((other) => other.relativePath !== opj.relativePath &&
            (0, project_detection_1.isSameOrDescendantDir)(opj.normDir, other.normDir) &&
            opj.normDir !== other.normDir);
    })
        .map((opj) => opj.relativePath));
    const isProjectAnchorOpj = (relativePath) => {
        if (!relativePath.toLowerCase().endsWith(OrCADXFileType.OrCADCaptureProject)) {
            return false;
        }
        return !sidecarOpjPaths.has(relativePath);
    };
    return (0, project_detection_1.detectAnchoredProjects)({
        tool: 'orcad',
        toolDisplayName: 'OrCAD',
        files,
        fileDetails,
        primaryAnchorMatch: isProjectAnchorOpj,
        primaryAnchorLabel: OrCADXFileType.OrCADCaptureProject,
    });
}
function validateOrCADXInternalNetlistFile(filename, fileContents) {
    if (!filename.toLowerCase().endsWith(OrCADXFileType.OrCADCaptureInternalNetlist)) {
        return undefined;
    }
    try {
        const result = (0, ONLParser_1.parseOrCADNetlist)(fileContents);
        if (!result.netlist) {
            return undefined;
        }
        const version = result.netlist.description?.program?.version;
        return {
            referencedFiles: [],
            metadata: {
                [OrCADXProjectMetadataKey.FileType]: 'OrCAD Capture Netlist',
                [OrCADXProjectMetadataKey.FileVersion]: version ?? 'unknown',
                [OrCADXProjectMetadataKey.ONLNetlist]: result.netlist,
            },
            version,
            warnings: result.processingResults,
        };
    }
    catch {
        return undefined;
    }
}
//# sourceMappingURL=orcadValidation.js.map